// Frame-accurate playback of an EDL: two stacked <video>s (the back one is preloaded on the
// next segment, so a cut swaps with no gap) + the scene narration kept in sync. Shared by the
// Edit step (scrub, edit) and the Finish step (full-cut preview).

import { useEffect, useRef, useState } from "react";
import {
  FPS,
  TRANSITION_FRAMES,
  clipLevel,
  effectiveTransition,
  locate,
  segmentStart,
  totalFrames,
} from "./edl";

const XF_MS = (TRANSITION_FRAMES / FPS) * 1000; // crossfade dissolve length

/** Zoom scale at `local` frames into a segment; progress clamps to the segment (handle frames hold the end). */
export function zoomScale(seg, local) {
  if (!seg.zoom) return 1;
  const p = Math.min(1, Math.max(0, local / (seg.outFrame - seg.inFrame)));
  return seg.zoom === "in" ? 1 + 0.1 * p : 1.1 - 0.1 * p;
}

/**
 * Live approximation of the server's effects for the frame at `local` of edl[index]:
 * zoom 1→1.1 (in) / 1.1→1 (out) over the segment, and the dip to black of a "fade" cut
 * (half out on the previous segment's tail, half in on this head). The render is exact.
 */
export function segmentFx(edl, index, local, takeFrames) {
  const seg = edl[index];
  const n = seg.outFrame - seg.inFrame;
  const half = TRANSITION_FRAMES / 2;
  const scale = zoomScale(seg, local);
  let opacity = 1;
  if (local < half && effectiveTransition(edl, index, takeFrames) === "fade")
    opacity = local / half;
  else if (
    local >= n - half &&
    edl[index + 1] &&
    effectiveTransition(edl, index + 1, takeFrames) === "fade"
  )
    opacity = (n - 1 - local) / half;
  return { scale, opacity };
}

/** Volume of a take's own sound in the preview: 0 for a take with no audio track (old silent takes). */
export const takeVolume = (take, clipAudio) => (take?.audioTrack ? clipLevel(clipAudio) : 0);

// Seek to the middle of take frame n (frame-exact on 24 fps CFR, 12-frame GOP — spec §9.4).
// Returns a cleanup when it has to wait for metadata.
function seekFrame(video, frame) {
  const seek = () => {
    try {
      video.currentTime = (frame + 0.5) / FPS;
    } catch {
      /* not seekable yet — loadedmetadata retries */
    }
  };
  if (video.readyState >= 1) {
    seek();
    return undefined;
  }
  video.addEventListener("loadedmetadata", seek, { once: true });
  return () => video.removeEventListener("loadedmetadata", seek);
}

const playSafe = (video) => video?.play?.()?.catch?.(() => {});

/** takeOf(segment) → its take; sound = play the narration (audioUrl) with the cut. */
export function useCutPlayer({ edl, takeOf, audioUrl, sound, clipAudio }) {
  const total = totalFrames(edl);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [front, setFront] = useState(0); // which of the two stacked <video>s shows the playhead
  const [xfSlot, setXfSlot] = useState(null); // <video> still dissolving out over a crossfade cut
  const xfRef = useRef(null); // { video, t0 }
  const videoRefs = useRef([null, null]);
  const audioRef = useRef(null);
  const frontRef = useRef(0);
  const segIdxRef = useRef(0); // segment being played
  const edlRef = useRef(edl);
  edlRef.current = edl;
  const takeOfRef = useRef(takeOf);
  takeOfRef.current = takeOf;
  const playheadRef = useRef(playhead);
  playheadRef.current = playhead;
  const clipAudioRef = useRef(clipAudio);
  clipAudioRef.current = clipAudio;

  // An op can shorten the video under the playhead.
  useEffect(() => {
    if (playhead > total) setPlayhead(total);
  }, [playhead, total]);

  const hit = locate(edl, Math.min(playhead, total - 1));
  const hitTake = hit ? takeOf(hit.segment) : null;
  const nextSeg = hit ? edl[hit.index + 1] : null; // preloaded in the back <video>
  const nextTake = nextSeg ? takeOf(nextSeg) : null;
  // Frame-exact scrub of the front <video> (take frame = inFrame + local frame). Playback drives it itself.
  const takeFrame = hit ? hit.segment.inFrame + hit.localFrame : null;
  const fx = hit ? segmentFx(edl, hit.index, hit.localFrame, (x) => takeOf(x)?.frames) : null;

  useEffect(() => {
    const video = videoRefs.current[front];
    if (!video || takeFrame === null || playing) return undefined;
    return seekFrame(video, takeFrame);
  }, [takeFrame, hitTake?.url, front, playing]);
  // The back <video> waits on the next segment's first frame, so a cut swaps with no gap.
  useEffect(() => {
    const video = videoRefs.current[1 - front];
    if (!video || !nextSeg || !nextTake?.url || xfSlot !== null) return undefined; // not while it dissolves out
    return seekFrame(video, nextSeg.inFrame);
  }, [front, nextSeg?.inFrame, nextTake?.url, xfSlot]);

  // Clip sound: only the front <video> is audible (and only if its take has a track); the back
  // buffer stays muted. A dissolving-out video keeps its state — the step loop ramps it down.
  const frontVolume = takeVolume(hitTake, clipAudio);
  useEffect(() => {
    videoRefs.current.forEach((v, slot) => {
      if (!v || slot === xfSlot) return;
      v.muted = slot !== front || frontVolume === 0;
      v.volume = slot === front ? frontVolume : 0;
    });
  }, [front, xfSlot, frontVolume, hitTake?.url, nextTake?.url]);

  // Narration follows the cut: play/pause with the video, and re-sync when scrubbing while idle.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    audio.currentTime = playheadRef.current / FPS;
    if (!playing) return undefined;
    playSafe(audio);
    return () => audio.pause?.();
  }, [playing, sound, audioUrl]);
  useEffect(() => {
    if (!playing && audioRef.current) audioRef.current.currentTime = playhead / FPS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playhead]);

  const stop = () => {
    setPlaying(false);
    videoRefs.current.forEach((v) => v?.pause?.());
  };
  const togglePlay = () => {
    if (playing) {
      stop();
    } else if (total > 0) {
      frontRef.current = front;
      if (playhead >= total) setPlayhead(0);
      playheadRef.current = playhead >= total ? 0 : playhead;
      setPlaying(true);
    }
  };
  // Any EDL change (not a poll's identical copy) ends playback.
  const edlKey = edl.map((x) => `${x.id}:${x.inFrame}:${x.outFrame}`).join();
  useEffect(() => {
    if (playing) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edlKey]);

  // The cut plays segment by segment: the playhead follows the front video's clock; at a
  // segment's outFrame the preloaded back video takes over at the next inFrame.
  useEffect(() => {
    if (!playing) return undefined;
    const list = edlRef.current;
    const start = locate(list, playheadRef.current);
    if (!start) {
      setPlaying(false);
      return undefined;
    }
    segIdxRef.current = start.index;
    const first = videoRefs.current[frontRef.current];
    const unseek = first ? seekFrame(first, start.segment.inFrame + start.localFrame) : undefined;
    playSafe(first);
    let raf;
    let since = performance.now(); // when the current segment's video started waiting
    const onError = () => setPlaying(false);
    first?.addEventListener?.("error", onError);
    const step = () => {
      const cur = edlRef.current;
      const i = segIdxRef.current;
      const sg = cur[i];
      const video = videoRefs.current[frontRef.current];
      if (!sg || !video) {
        setPlaying(false);
        return;
      }
      const xf = xfRef.current;
      if (xf) {
        const k = (performance.now() - xf.t0) / XF_MS;
        if (k >= 1) {
          xf.video.pause?.();
          xf.video.style.opacity = "";
          xf.video.style.transform = "";
          xfRef.current = null;
          setXfSlot(null);
        } else {
          // The dissolving video is no longer the front slot, so React no longer styles it: keep its zoom here.
          xf.video.style.opacity = String(1 - k);
          if (!xf.video.muted)
            xf.video.volume =
              takeVolume(takeOfRef.current(cur[xf.index]), clipAudioRef.current) * (1 - k);
          xf.video.style.transform = `scale(${zoomScale(cur[xf.index], Math.floor(xf.video.currentTime * FPS) - cur[xf.index].inFrame)})`;
        }
      }
      // A take with no url, or a video that has loaded nothing after 2 s, can never play: stop.
      const tk = takeOfRef.current(sg);
      if (!tk?.url || (video.readyState === 0 && performance.now() - since > 2000)) {
        video.pause?.();
        setPlaying(false);
        return;
      }
      const f = Math.floor(video.currentTime * FPS);
      if (f >= sg.outFrame) {
        const nx = cur[i + 1];
        // Crossfade: the outgoing video keeps playing its handle frames and dissolves out over the next one.
        const dissolve =
          !!nx &&
          !xfRef.current &&
          effectiveTransition(cur, i + 1, (x) => takeOfRef.current(x)?.frames) === "crossfade";
        if (dissolve) {
          xfRef.current = { video, t0: performance.now(), index: i };
          setXfSlot(frontRef.current);
        } else video.pause?.();
        if (!nx) {
          setPlayhead(totalFrames(cur));
          setPlaying(false);
          return;
        }
        const other = 1 - frontRef.current;
        const nv = videoRefs.current[other];
        if (nv && Math.abs(nv.currentTime * FPS - (nx.inFrame + 0.5)) > 1) {
          nv.currentTime = (nx.inFrame + 0.5) / FPS;
        }
        playSafe(nv);
        const audio = audioRef.current;
        const want = segmentStart(cur, i + 1) / FPS;
        if (audio && Math.abs(audio.currentTime - want) > 0.1) audio.currentTime = want;
        since = performance.now();
        nv?.addEventListener?.("error", onError);
        segIdxRef.current = i + 1;
        frontRef.current = other;
        setFront(other);
        setPlayhead(segmentStart(cur, i + 1));
      } else {
        setPlayhead(segmentStart(cur, i) + Math.max(0, f - sg.inFrame));
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      unseek?.();
      cancelAnimationFrame(raf);
      if (xfRef.current) {
        xfRef.current.video.style.opacity = "";
        xfRef.current.video.style.transform = "";
        xfRef.current = null;
        setXfSlot(null);
      }
      videoRefs.current.forEach((v) => v?.removeEventListener?.("error", onError));
    };
  }, [playing]);

  return {
    total,
    playhead,
    setPlayhead,
    playing,
    front,
    xfSlot,
    fx,
    videoRefs,
    audioRef,
    hit,
    hitTake,
    nextTake,
    takeFrame,
    stop,
    togglePlay,
  };
}

/** The two stacked <video>s (+ the narration <audio> when sound is on) driven by useCutPlayer. */
export function CutVideos({ player, sound, audioUrl }) {
  const { videoRefs, front, xfSlot, fx, hitTake, nextTake, audioRef } = player;
  return (
    <>
      {[0, 1].map((slot) => {
        const url = slot === front ? hitTake?.url : nextTake?.url;
        return (
          url && (
            <video
              key={slot}
              ref={(el) => {
                videoRefs.current[slot] = el;
              }}
              src={url}
              muted
              playsInline
              preload="auto"
              data-front={slot === front}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                visibility: slot === front || slot === xfSlot ? "visible" : "hidden",
                zIndex: slot === xfSlot ? 3 : slot === front ? 2 : 1,
                ...(slot === front && fx
                  ? { transform: `scale(${fx.scale})`, opacity: fx.opacity }
                  : {}),
              }}
            />
          )
        );
      })}
      {sound && audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" />}
    </>
  );
}
